import { useSEO } from "@/hooks/useSEO";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Car, AlertTriangle, Compass, CircleDot, Fuel, ParkingSquare, PhoneOff, ShieldCheck, Timer } from "lucide-react";

const TIPS = [
  { icon: Car, t: "Keep left — always", d: "In the Bahamas you drive on the LEFT side of the road, like the UK. Most rental cars are left-hand drive (US-style), so take an extra beat at every turn and repeat it to yourself each morning: keep left." },
  { icon: CircleDot, t: "Roundabouts: yield to the right", d: "Enter roundabouts clockwise and yield to traffic already in the circle coming from your right. Nassau's roundabouts on West Bay Street and near the fish fry are the busiest — signal your exit." },
  { icon: Timer, t: "Take it slow", d: "Limits are modest and posted in miles per hour: roughly 25 mph in town and 45 mph on open stretches. Speed bumps appear with little warning, especially in residential areas and near schools." },
  { icon: AlertTriangle, t: "Watch for jitneys", d: "Local buses (jitneys) stop anywhere a passenger waves — no marked bays. Expect sudden stops and never tailgate one; locals simply wait or pass when it's clearly safe." },
  { icon: Compass, t: "One-way streets downtown", d: "Downtown Nassau around Bay Street is a grid of one-way streets. Miss your turn and it's easier to loop the block than to fight it. Your rental's GPS option comes preloaded with island maps." },
  { icon: ParkingSquare, t: "Parking is easy — with care", d: "Most hotels, beaches and restaurants have free parking. Downtown, use marked bays and don't block driveways or jitney stops. Never leave valuables visible in the car." },
  { icon: Fuel, t: "Fuel up before exploring", d: "Gas stations cluster along Bay Street, West Bay Street and the main corridor to the airport — they thin out on the east side. Return your rental at the same fuel level it left with." },
  { icon: PhoneOff, t: "Hands off the phone", d: "Using a hand-held phone while driving is illegal in the Bahamas. Set your route before you pull away, or let a passenger navigate." },
  { icon: ShieldCheck, t: "Rain & night driving", d: "Tropical showers make roads slick fast, and street lighting outside the main corridors is limited. Slow down, increase following distance, and watch for pedestrians walking along the road shoulder." },
];

export default function GuideDriving() {
  useSEO({
    title: "Driving in Nassau Bahamas — 9 Left-Side Driving Tips | Easy Drive",
    description: "First time driving in the Bahamas? Our local guide to driving on the left in Nassau: roundabouts, jitneys, speed limits, parking and fuel — from the Easy Drive Car Rental team.",
    path: "/guides/driving-in-nassau",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: "Driving in Nassau, Bahamas: 9 Left-Side Driving Tips for Visitors",
      description: "A local guide to driving on the left in Nassau — roundabouts, jitneys, speed limits, parking and fuel tips for visitors renting a car in the Bahamas.",
      author: { "@type": "Organization", name: "Easy Drive Car Rental", url: "https://easydrivecarrental.com/" },
      publisher: { "@type": "Organization", name: "Easy Drive Car Rental", logo: { "@type": "ImageObject", url: "https://easydrivecarrental.com/logo.png" } },
      mainEntityOfPage: "https://easydrivecarrental.com/guides/driving-in-nassau",
      datePublished: "2026-09-20",
      dateModified: "2026-09-20",
    },
  });

  return (
    <div data-testid="guide-driving-page" className="max-w-4xl mx-auto px-6 lg:px-10 pt-36 pb-24">
      <p className="text-[11px] font-ui tracking-[0.3em] uppercase text-primary">Island Guide</p>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight leading-tight">
        Driving in Nassau: <span className="italic gold-text">9 left-side tips</span> for visitors
      </h1>
      <p className="mt-6 text-muted-foreground leading-relaxed font-ui max-w-2xl">
        Yes — in the Bahamas we drive on the <strong className="text-foreground">left</strong>. If you're visiting from the US, Canada or mainland Europe it takes about a day to feel natural, and these local tips will get you there faster. Written by the Easy Drive team, born and raised on West Bay Street.
      </p>

      <div className="mt-12 space-y-5">
        {TIPS.map(({ icon: Icon, t, d }, i) => (
          <div key={i} data-testid={`guide-tip-${i + 1}`} className="bg-card border border-border rounded-2xl p-6 md:p-7 flex gap-5 transition-colors duration-200 hover:border-primary/40">
            <span className="w-11 h-11 rounded-full border border-primary/30 flex items-center justify-center shrink-0">
              <Icon size={18} className="gold-text" />
            </span>
            <div>
              <h2 className="font-display text-xl md:text-2xl tracking-tight">{i + 1}. {t}</h2>
              <p className="mt-2 text-sm text-muted-foreground leading-relaxed font-ui">{d}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="mt-14 rounded-2xl border border-primary/25 bg-card p-8 md:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6 shadow-[0_8px_30px_rgba(224,36,52,0.06)]">
        <div>
          <h2 className="font-display text-2xl md:text-3xl tracking-tight">Ready to explore the island?</h2>
          <p className="mt-2 text-sm text-muted-foreground font-ui max-w-md">Every Easy Drive rental comes with unlimited mileage on New Providence and free hotel, villa &amp; cruise port delivery.</p>
        </div>
        <div className="flex flex-wrap gap-3">
          <Link to="/fleet">
            <Button data-testid="guide-driving-fleet-cta" className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui px-7 h-11">
              Browse the Fleet <ArrowRight size={15} className="ml-2" />
            </Button>
          </Link>
          <Link data-testid="guide-driving-bridge-link" to="/guides/paradise-island-bridge-toll" className="inline-flex items-center text-sm font-ui font-semibold text-primary hover:underline underline-offset-4 self-center">
            Next guide: Paradise Island bridge tolls →
          </Link>
        </div>
      </div>
    </div>
  );
}
